import { useState } from "react";
import { ArrowUpRight, Play } from "lucide-react";
import { Reveal, Eyebrow, RevealImage } from "./Reveal";
import Magnetic from "./Magnetic";
import { scrollToId } from "@/lib/scroll";

const POINTS = [
  { title: "Product enquiry", note: "Availability, pricing and ordering for your clinic." },
  { title: "Clinic onboarding", note: "Become an official Skinfill Bacio clinic in AU or NZ." },
  { title: "Education & training", note: "Product training and professional education enquiries." },
  { title: "Wholesale & distribution", note: "Distribution enquiries across Australia and New Zealand." },
];

const Professionals = () => {
  const [playing, setPlaying] = useState(false);

  return (
  <section id="professionals" data-testid="professionals-section" className="bg-[#1C1417] py-24 sm:py-36">
    <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-14 items-start">
      <div>
        <Reveal>
          <Eyebrow light>For aesthetic professionals</Eyebrow>
        </Reveal>
        <Reveal delay={0.08}>
          <h2 className="font-serif font-light text-[#FAF7F5] text-3xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] mt-6 text-balance">
            Offer the lip boost your clients are <span className="italic text-[#E893AE]">asking for by name.</span>
          </h2>
        </Reveal>
        <Reveal delay={0.16}>
          <p className="mt-7 text-[#F4EBE6]/70 text-base sm:text-lg font-light leading-relaxed max-w-lg">
            Skinfill Bacio is available to eligible clinics and aesthetic professionals across Australia and
            New Zealand through Dermalis — the official distributor.
          </p>
        </Reveal>
        <Reveal delay={0.24}>
          <div className="mt-10">
            <Magnetic>
              <button
                data-testid="professionals-become-clinic-button"
                onClick={() => scrollToId("#contact")}
                className="btn-lux btn-lux-magenta group"
              >
                <span>Become a Bacio Clinic</span>
                <ArrowUpRight
                  size={16}
                  className="relative z-[1] transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                />
              </button>
            </Magnetic>
          </div>
        </Reveal>
      </div>

      <div className="grid sm:grid-cols-2 gap-4 lg:pt-14">
        {POINTS.map((p, i) => (
          <Reveal key={p.title} delay={0.1 + i * 0.08}>
            <div className="rounded-3xl border border-white/10 bg-white/[0.04] p-7 hover:bg-white/[0.08] hover:border-[#D84A75]/40 transition-colors duration-400 h-full">
              <p className="font-mono text-[10px] tracking-[0.3em] text-[#E893AE]">0{i + 1}</p>
              <p className="font-serif text-xl text-[#FAF7F5] mt-4">{p.title}</p>
              <p className="text-xs text-[#F4EBE6]/60 font-light leading-relaxed mt-2.5">{p.note}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>

    <div className="max-w-7xl mx-auto px-5 sm:px-8 mt-20 sm:mt-28">
      <Reveal>
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <Eyebrow light>Watch</Eyebrow>
            <h3 className="font-serif font-light text-[#FAF7F5] text-3xl sm:text-5xl tracking-tight mt-4 leading-[1.05]">
              Inside the <span className="italic text-[#E893AE]">formula.</span>
            </h3>
          </div>
          <p className="font-mono text-[10px] sm:text-xs uppercase tracking-[0.22em] text-[#F4EBE6]/50 max-w-xs leading-relaxed">
            Professional product presentation — hyaluronic acid &amp; vitamin B12
          </p>
        </div>
      </Reveal>

      <RevealImage delay={0.12} className="mt-10">
        <div
          data-testid="professional-video-container"
          className="relative rounded-[28px] sm:rounded-[36px] overflow-hidden border border-white/10 shadow-[0_60px_120px_-50px_rgba(163,29,79,0.4)]"
        >
          {playing ? (
            <video
              data-testid="professional-video"
              className="w-full aspect-video bg-black"
              controls
              autoPlay
              playsInline
              poster="/assets/professional-poster.webp"
            >
              <source src="/assets/bacio-professional.mp4" type="video/mp4" />
              <source src="/assets/bacio-professional.webm" type="video/webm" />
            </video>
          ) : (
            <button
              data-testid="professional-video-play"
              onClick={() => setPlaying(true)}
              className="relative block w-full aspect-video group cursor-pointer"
              aria-label="Play the Skinfill Bacio professional video"
            >
              <img
                src="/assets/professional-poster.webp"
                alt="Skinfill Bacio professional video preview"
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.03]"
              />
              <span className="absolute inset-0 bg-[#1C1417]/35 group-hover:bg-[#1C1417]/25 transition-colors duration-500" />
              <span className="absolute inset-0 flex items-center justify-center">
                <span className="w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-white/15 backdrop-blur-md border border-white/40 flex items-center justify-center transition-transform duration-500 group-hover:scale-110">
                  <Play size={26} className="text-white ml-1" fill="white" />
                </span>
              </span>
              <span className="absolute bottom-5 left-6 font-mono text-[10px] uppercase tracking-[0.3em] text-white/80">
                Skinfill Bacio® — Professional
              </span>
            </button>
          )}
        </div>
      </RevealImage>
    </div>
  </section>
  );
};

export default Professionals;
